"use client";
import React from "react";
import { twJoin, twMerge } from "tailwind-merge";
import usePaymentCurrency from "@/shared/hooks/usePaymentCurrency";
import useCryptoInUSD from "@/shared/hooks/useCryptoInUSD";
import useDisplayPrice from "@/shared/hooks/useDisplayPrice";
import { CryptoCurrencies, CryptoCurrencyID } from "@/shared/constants";
import { ThemedIcon } from "../Icon";
import Image from "next/image";
import TurboIcon from "@/assets/icons/turbo.png";
export interface Props {
  cryptoValue?: string;
  paymentErc20TokenAddress?: string;
  className?: string;
  oneLine?: boolean;
  roundToDecimalCount?: number;
  hideIcon?: boolean;
  isLightEther?: boolean;
  iconClassName?: string;
  priceClassName?: string;
  usdPriceClassName?: string;
}

const CryptoValue = ({
  cryptoValue,
  paymentErc20TokenAddress,
  className,
  oneLine,
  roundToDecimalCount,
  hideIcon,
  isLightEther,
  iconClassName,
  priceClassName,
  usdPriceClassName,
}: Props) => {
  const IconByChain = {
    [CryptoCurrencyID.ETH]: (
      <ThemedIcon
        className={twMerge("mr-2 w-8 h-8", iconClassName)}
        iconName={
          isLightEther
            ? CryptoCurrencies.Ether
            : `${CryptoCurrencies.Ether}-solid`
        }
      />
    ),
    [CryptoCurrencyID.POL]: (
      <ThemedIcon
        className={twMerge("mr-2 w-8 h-8", iconClassName)}
        iconName={CryptoCurrencies.POL}
      />
    ),
    [CryptoCurrencyID.WPOL]: (
      <ThemedIcon
        className={twMerge("mr-2 w-8 h-8", iconClassName)}
        iconName={CryptoCurrencies.WPOL}
      />
    ),
    [CryptoCurrencyID.WETH]: (
      <ThemedIcon
        className={twMerge("mr-2 w-8 h-8", iconClassName)}
        iconName={CryptoCurrencies.Weth}
      />
    ),
    [CryptoCurrencyID.USDT]: (
      <ThemedIcon
        className={twMerge("mr-2 w-8 h-8", iconClassName)}
        iconName={CryptoCurrencies.USDT}
      />
    ),
    [CryptoCurrencyID.TURBO]: (
      <Image
        src={TurboIcon.src}
        alt="Turbo"
        width={32}
        height={32}
        className={twMerge(iconClassName, "mr-2 w-8 h-8")}
      />
    ),
  };

  const paymentMethod = usePaymentCurrency(paymentErc20TokenAddress);

  const displayPrice = useDisplayPrice(
    cryptoValue,
    paymentMethod?.decimals,
    roundToDecimalCount,
  );

  const { cryptoInUSD: priceInUSD } = useCryptoInUSD(
    displayPrice,
    paymentMethod?.id,
  );

  return (
    <div className={twMerge("flex items-center justify-start", className)}>
      {!hideIcon && <span>{IconByChain[paymentMethod?.id || "ethereum"]}</span>}
      <div
        className={twMerge(
          "flex",
          oneLine
            ? "text-right items-center justify-end sm:text-left flex-wrap"
            : "items-end lg:items-start flex-col",
        )}
      >
        <div>
          <span
            className={twMerge(
              (priceInUSD || priceInUSD === 0) && "text-lg font-light",
              priceClassName,
            )}
          >
            {displayPrice || "-"}
            <span className="ml-2 mt-2">{paymentMethod?.label}</span>
          </span>
        </div>
        {(priceInUSD || priceInUSD === 0) && (
          <div
            className={twJoin(
              oneLine ? "ml-2 text-base" : "text-sm endemic:text-primary",
              "text-secondary font-light",
              usdPriceClassName,
            )}
          >
            {oneLine && "("}
            {priceInUSD} USD{oneLine && ")"}
          </div>
        )}
      </div>
    </div>
  );
};

export default CryptoValue;
